import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { user } from '../../auth'
import { AchievementCondition } from '../../content'

export const AchievementProgress = pgTable(
  'achievement_progress',
  {
    id: uuid().primaryKey().defaultRandom(),
    achievementConditionId: uuid()
      .notNull()
      .references(() => AchievementCondition.id, { onDelete: 'cascade' }),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),

    currentValue: numericAsNumber().default(0),
    isCompleted: boolean().notNull().default(false),
    completedAt: timestamp({ mode: 'date', withTimezone: true }),

    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_achievement_progress_user').on(t.userId),
    index('idx_achievement_progress_condition').on(t.achievementConditionId),
  ],
)

export const achievementProgressRelations = relations(AchievementProgress, ({ one }) => ({
  achievementCondition: one(AchievementCondition, {
    fields: [AchievementProgress.achievementConditionId],
    references: [AchievementCondition.id],
  }),

  user: one(user, {
    fields: [AchievementProgress.userId],
    references: [user.id],
  }),
}))

export const InsertAchievementProgressSchema = createInsertSchema(AchievementProgress).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectAchievementProgressSchema = createSelectSchema(AchievementProgress)
export const UpdateAchievementProgressSchema = createUpdateSchema(AchievementProgress)
  .partial()
  .omit({
    id: true,
    userId: true,
    achievementConditionId: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    // If marking as completed, completedAt should be provided
    if (data.isCompleted === true && !data.completedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'completedAt is required when marking as completed',
        path: ['completedAt'],
      })
    }

    // If not completed, completedAt should not be set
    if (data.isCompleted === false && data.completedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'completedAt should not be set when isCompleted is false',
        path: ['completedAt'],
      })
    }
  })
export type InsertAchievementProgress = typeof AchievementProgress.$inferInsert
export type UpdateAchievementProgress = z.infer<typeof UpdateAchievementProgressSchema>
export type SelectAchievementProgress = typeof AchievementProgress.$inferSelect
